import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { API, errMsg } from './careApi';
import { Btn } from './careUi';
import { ResidentFields } from './CareResidentForm';

const EMPTY = {
  name: '', nric: '', phone: '', email: '', dob: '', gender: '', address: '', admission_date: '', room: '', monthly_amount: 0,
  representative_name: '', representative_relationship: '', representative_ic: '', representative_phone: '', representative_email: '',
};

export default function CarePublicRegister() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(slug ? null : false);
  const invalid = !!slug && org === false;
  useEffect(() => { if (slug) axios.get(`${API}/public/org/${slug}`).then((r) => setOrg(r.data)).catch(() => setOrg(false)); }, [slug]);
  const [f, setF] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState('');

  const submit = async () => {
    if (!f.name.trim() || !f.nric.trim() || !f.representative_name.trim() || !f.representative_phone.trim()) {
      return toast.error('Resident name, NRIC/passport, representative name and contact number are required');
    }
    if (!consent) return toast.error('Please tick the confirmation box');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/register/${slug}`, { ...f, room: '', monthly_amount: 0, consent, website: hp });
      if (data.sign_token) { toast.success(`Registration received (${data.reference}). Please complete the admission documents.`); return navigate(`/care/sign/${data.sign_token}`); }
      setRef(data.reference || 'received');
      window.scrollTo(0, 0);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (org === null) return <div className="min-h-screen bg-slate-50" />;
  if (org === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="register-no-company">
        <div className="max-w-md bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <p className="text-xs font-semibold text-teal-700 tracking-wide">STAYMII CARE</p>
          <h1 className="text-xl font-bold text-slate-900 mt-2" data-testid={invalid ? 'register-invalid-link' : undefined}>{invalid ? 'This registration link is not valid' : "Please use your care home's registration link"}</h1>
          <p className="text-sm text-slate-500 mt-2">Each care home has its own link, for example staymii.com/care/register/your-care-home. Please ask the care home to send it to you.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="care-public-register">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs font-semibold text-teal-700 tracking-wide" data-testid="register-org-name">{org.company_name || org.name}</p>
        <h1 className="text-3xl font-bold text-[#181B30] mt-1">Resident registration</h1>
        <p className="text-sm text-slate-500 mt-2">Family members or representatives can register a new resident here. After submitting, you will be taken straight to the Admission Form, PDPA Consent, Resident Contract and Admission Checklist to complete and sign.</p>
        <Link to="/staymii-care/guide" target="_blank" rel="noopener" data-testid="register-guide-link"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal-50 border border-teal-200 px-3.5 py-1.5 text-sm font-semibold text-teal-800 hover:bg-teal-100 transition-colors">
          <BookOpen className="w-4 h-4" /> How to fill the forms
        </Link>
        {ref ? (
          <div className="mt-8 bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="register-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="mt-3 font-semibold text-slate-900">Registration received</p>
            <p className="text-sm text-slate-500 mt-1">Reference: <b data-testid="register-reference">{ref}</b>. The care home will contact you shortly.</p>
          </div>
        ) : (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
            <ResidentFields f={f} set={setF} publicMode />
            <input className="hidden" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} name="website" />
            <label className="flex items-start gap-2.5 mt-6 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-teal-700" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="register-consent" />
              I confirm the information is true and accurate, and I agree to be contacted by the care home about this registration.
            </label>
            <Btn className="mt-5" loading={busy} onClick={submit} data-testid="register-submit">Submit registration</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
